import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, MessageCircle, PartyPopper, Send, Sparkles, Trash2, Info, ImagePlus, X, MessagesSquare, Newspaper, Crown } from "lucide-react";
import { socialApi, sportsApi, errMsg } from "@/lib/api";
import { compressImage } from "@/lib/imageCompress";
import { useAuth } from "@/context/AuthContext";
import { PageContainer, SectionTitle, EmptyState, UserAvatar } from "@/components/shared";
import { Reveal, StaggerGroup, StaggerItem, SkeletonList, LazyImage } from "@/components/motion";
import { VedettesDuMois } from "@/components/social/VedettesDuMois";
import { MurHonneur } from "@/components/social/MurHonneur";
import { cn } from "@/lib/utils";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";

const timeAgo = (iso) => { try { return formatDistanceToNow(new Date(iso), { addSuffix: true, locale: fr }); } catch { return ""; } };

const AUTO_LABEL = { trophy: "Trophée", event: "Évènement", collecte: "Cotisation", vote: "Vote", info: "Info" };

function PostCard({ post, onChange, canInteract }) {
  const { user } = useAuth();
  const [liked, setLiked] = useState(post.liked);
  const [likes, setLikes] = useState(post.likes_count);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");

  const like = async () => { if (!canInteract) { toast.error("Réservé aux membres actifs"); return; } try { const r = await socialApi.like(post.id); setLiked(r.data.liked); setLikes(r.data.likes_count); } catch (e) { toast.error(errMsg(e)); } };
  const congrats = async () => { if (!canInteract) { toast.error("Réservé aux membres actifs"); return; } try { await socialApi.addComment(post.id, "Félicitations ! \uD83D\uDC4F"); toast.success("Félicitations envoyées"); if (showComments) loadComments(); } catch (e) { toast.error(errMsg(e)); } };
  const loadComments = async () => { const r = await socialApi.comments(post.id); setComments(r.data); };
  const toggleComments = async () => { const n = !showComments; setShowComments(n); if (n) loadComments(); };
  const addComment = async () => { if (!text.trim()) return; try { await socialApi.addComment(post.id, text); setText(""); loadComments(); } catch (e) { toast.error(errMsg(e)); } };
  const del = async () => { try { await socialApi.deletePost(post.id); toast.success("Publication supprimée"); onChange(); } catch (e) { toast.error(errMsg(e)); } };

  const authorName = post.author ? `${post.author.first_name} ${post.author.last_name}` : "ASL";
  return (
    <Card data-testid="publication-carte" className="overflow-hidden transition-shadow duration-200 hover:shadow-md"><CardContent className="p-4">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          {post.author?.id ? (
            <Link to={`/profil/${post.author.id}`}><UserAvatar user={post.author} size="h-9 w-9" /></Link>
          ) : <UserAvatar user={post.author || { first_name: "A", last_name: "S" }} size="h-9 w-9" />}
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold">
              {post.author?.id ? <Link to={`/profil/${post.author.id}`} className="hover:underline">{authorName}</Link> : authorName}
              {post.sport_name && <Badge variant="outline" className="text-[10px]">{post.sport_name}</Badge>}
            </div>
            <div className="text-[11px] text-muted-foreground">{timeAgo(post.created_at)}</div>
          </div>
        </div>
        {post.auto ? <Badge variant="outline" className="gap-1 text-[10px]"><Info className="h-3 w-3" />{AUTO_LABEL[post.auto_type] || "Auto"}</Badge>
          : (user && (user.role === "admin" || user.id === post.author?.id)) && <button onClick={del} className="text-muted-foreground transition-colors hover:text-rose-600" data-testid="publication-supprimer"><Trash2 className="h-4 w-4" /></button>}
      </div>
      <p className="mt-3 whitespace-pre-wrap text-sm">{post.content}</p>
      {post.image && <LazyImage src={post.image} alt="" className="mt-3 max-h-96 w-full rounded-xl" imgClassName="object-cover" />}
      <div className="mt-3 flex items-center gap-1 border-t pt-2">
        <Button variant="ghost" size="sm" onClick={like} className={liked ? "text-rose-600" : ""} data-testid="publication-like-bouton">
          <motion.span initial={false} animate={{ scale: liked ? [1, 1.35, 1] : 1 }} transition={{ duration: 0.3 }} className="mr-1.5 inline-flex"><Heart className={`h-4 w-4 ${liked ? "fill-rose-600" : ""}`} /></motion.span>{likes}
        </Button>
        <Button variant="ghost" size="sm" onClick={toggleComments} data-testid="publication-commenter-bouton"><MessageCircle className="mr-1.5 h-4 w-4" />{post.comments_count}</Button>
        <Button variant="ghost" size="sm" onClick={congrats} data-testid="publication-feliciter-bouton"><PartyPopper className="mr-1.5 h-4 w-4" />Féliciter</Button>
      </div>
      <AnimatePresence initial={false}>
        {showComments && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }} className="overflow-hidden">
            <div className="mt-3 space-y-3 border-t pt-3">
              {comments.map((c) => (
                <div key={c.id} className="flex items-start gap-2"><UserAvatar user={c.author} size="h-7 w-7" /><div className="rounded-xl bg-muted px-3 py-1.5"><div className="text-xs font-semibold">{c.author?.first_name} {c.author?.last_name}</div><div className="text-sm">{c.content}</div></div></div>
              ))}
              {canInteract && (
                <div className="flex items-center gap-2"><Textarea rows={1} value={text} onChange={(e) => setText(e.target.value)} placeholder="Écrire un commentaire…" className="min-h-0 py-2" data-testid="commentaire-input" /><Button size="icon" onClick={addComment} className="bg-secondary hover:bg-emerald-600 text-white" data-testid="commentaire-envoyer"><Send className="h-4 w-4" /></Button></div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </CardContent></Card>
  );
}

export default function SocialPage() {
  const { user } = useAuth();
  const fileRef = useRef(null);
  const [posts, setPosts] = useState([]);
  const [sports, setSports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [view, setView] = useState("fil");
  const [content, setContent] = useState("");
  const [image, setImage] = useState(null);
  const [postSport, setPostSport] = useState("general");
  const [busy, setBusy] = useState(false);

  const canInteract = user && (user.status === "active" || user.role === "admin" || user.role === "coach");

  const load = async () => { setLoading(true); try { const [p, s] = await Promise.all([socialApi.feed(filter !== "all" ? filter : undefined), sportsApi.list()]); setPosts(p.data); setSports(s.data); } finally { setLoading(false); } };
  useEffect(() => { load(); }, [filter]);

  const onImage = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Image trop lourde (max 10 Mo)"); return; }
    try { setImage(await compressImage(file)); } catch { toast.error("Impossible de traiter l'image"); }
  };

  const publish = async () => {
    if (!content.trim() && !image) return;
    setBusy(true);
    try { await socialApi.createPost({ content, image, sport_id: postSport === "general" ? null : postSport }); setContent(""); setImage(null); if (fileRef.current) fileRef.current.value = ""; toast.success("Publié !"); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <PageContainer className={view === "honneur" ? "max-w-5xl" : "max-w-3xl"}>
      <SectionTitle>Réseau</SectionTitle>
      <div className="-mt-3 inline-flex rounded-lg border p-0.5" role="group" aria-label="Vue du réseau">
        <button onClick={() => setView("fil")} aria-pressed={view === "fil"} data-testid="reseau-vue-fil" className={cn("inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors", view === "fil" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground")}><Newspaper className="h-4 w-4" />Fil d'actualité</button>
        <button onClick={() => setView("honneur")} aria-pressed={view === "honneur"} data-testid="reseau-vue-honneur" className={cn("inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors", view === "honneur" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground")}><Crown className="h-4 w-4" />Mur d'honneur</button>
      </div>

      {view === "honneur" ? <MurHonneur /> : (
        <>
          <VedettesDuMois canInteract={canInteract} />
          <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
            <Button variant={filter === "all" ? "default" : "outline"} size="sm" onClick={() => setFilter("all")} data-testid="social-filtre-general">Général</Button>
            {sports.map((s) => <Button key={s.id} variant={filter === s.id ? "default" : "outline"} size="sm" onClick={() => setFilter(s.id)} className="whitespace-nowrap">{s.name}</Button>)}
          </div>

      {user && (canInteract ? (
        <Reveal>
          <Card className="overflow-hidden"><CardContent className="space-y-3 p-4">
            <div className="flex items-start gap-3">
              <UserAvatar user={user} size="h-9 w-9" />
              <Textarea value={content} onChange={(e) => setContent(e.target.value)} placeholder="Partagez quelque chose avec la communauté…" className="min-h-[64px] flex-1" data-testid="fil-publication-texte-textarea" />
            </div>
            <AnimatePresence>
              {image && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="relative overflow-hidden">
                  <img src={image} alt="aperçu" className="max-h-64 w-full rounded-xl object-cover" />
                  <button type="button" onClick={() => { setImage(null); if (fileRef.current) fileRef.current.value = ""; }} className="absolute right-2 top-2 rounded-full bg-black/60 p-1 text-white transition-colors hover:bg-black/80" data-testid="fil-publication-image-retirer"><X className="h-4 w-4" /></button>
                </motion.div>
              )}
            </AnimatePresence>
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Select value={postSport} onValueChange={setPostSport}><SelectTrigger className="w-36 sm:w-44" data-testid="fil-publication-sport-select"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="general">Général</SelectItem>{sports.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent></Select>
                <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border px-2.5 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted" data-testid="fil-publication-image-bouton">
                  <ImagePlus className="h-4 w-4" /><span className="hidden sm:inline">Image</span>
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onImage} data-testid="fil-publication-image-input" />
                </label>
              </div>
              <Button onClick={publish} disabled={busy || (!content.trim() && !image)} className="bg-secondary hover:bg-emerald-600 text-white" data-testid="fil-publication-envoyer-bouton"><Sparkles className="mr-1.5 h-4 w-4" />Publier</Button>
            </div>
          </CardContent></Card>
        </Reveal>
      ) : (
        <Reveal><Card className="border-amber-200 bg-amber-50 dark:border-amber-500/30 dark:bg-amber-500/10"><CardContent className="p-4 text-sm text-amber-900 dark:text-amber-200">Seuls les <b>membres actifs</b> peuvent publier. Payez une cotisation de session pour débloquer le réseau social.</CardContent></Card></Reveal>
      ))}

      {loading ? <SkeletonList count={4} /> : posts.length === 0 ? <EmptyState title="Aucune publication" description="Soyez le premier à partager quelque chose avec la communauté." icon={MessagesSquare} /> : (
        <StaggerGroup className="space-y-4">{posts.map((p) => <StaggerItem key={p.id}><PostCard post={p} onChange={load} canInteract={canInteract} /></StaggerItem>)}</StaggerGroup>
      )}
        </>
      )}
    </PageContainer>
  );
}
